// static/js/components/ui/MarkdownDataResult.jsx

/**
 * MarkdownDataResult Component
 *
 * This component renders markdown content dynamically within the web application interface. It supports adjustable
 * viewing sizes and has a collapsible functionality to either show a preview (first 800 characters) or the full content.
 * The component also uses a dynamic class application based on the specified 'size' prop to control text sizing, 
 * supporting small, base, and large sizes.
 *
 * The primary features include:
 * - Displaying markdown formatted text using the ReactMarkdown component.
 * - Collapsible view that shows a brief preview or the full content with a toggle button.
 * - Customizable text size through the 'size' prop with predefined style settings.
 * - Stylistic enhancements such as background color and overflow control for improved UI integration.
 *
 * Props:
 * - data (string): The markdown content to be displayed.
 * - size (string): Optional. Controls the text size of the markdown content. Defaults to 'sm'.
 *
 * Usage:
 * Used to display markdown content in a collapsible format with adjustable size options, enhancing content readability 
 * and interface aesthetics.
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChevronDown, ChevronRight } from 'lucide-react';
import DOMPurify from 'dompurify'; // markdown sanitizer


// Configures DOMPurify to enforce security measures
DOMPurify.addHook('afterSanitizeAttributes', function(node) {
  // Set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  
  // Set non-HTML/MathML links to xlink:show=new
  if (!node.hasAttribute('target') && 
      (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('xlink:show', 'new');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// Define allowed markdown elements and attributes
const safeMarkdownConfig = {
  // Security configuration for ReactMarkdown
  allowedElements: [
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'code',
    'pre', 'em', 'strong', 'del', 'table',
    'tbody', 'tr', 'th', 'td', 'hr', 'br'
  ],
  // Disallow potentially dangerous elements
  disallowedElements: ['script', 'style', 'iframe', 'form', 'input', 'button'],
  // Restrict attributes to safe ones only
  allowedAttributes: {
    'a': ['href', 'title', 'target', 'rel'],
    'img': ['src', 'alt', 'title', 'width', 'height'],
    'code': ['className']
  },
  // Transform link URIs to ensure they're safe
  transformLinkUri: (uri) => {
    // Only allow http and https protocols
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      return uri;
    }
    return '#'; // Safety fallback
  }
};

const MarkdownDataResult = ({ data, size = 'sm' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data) return null;

  const previewContent = data.slice(0, 800) + (data.length > 800 ? '...' : '');

  const sizeClasses = {
    sm: 'prose-sm',
    base: 'prose-base',
    lg: 'prose-lg'
  };

  // Apply DOMPurify sanitization before rendering
  const sanitizeContent = (content) => {
    return DOMPurify.sanitize(content, {
      FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'button', 'object', 'embed', 'base'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onmousedown', 
                    'onmouseup', 'onkeydown', 'onkeypress', 'onkeyup', 'formaction',
                    'javascript:', 'data:', 'vbscript:'],
      ADD_URI_SAFE_ATTR: ['target', 'rel'],
      ADD_ATTR: ['target', 'rel'],
      ALLOW_UNKNOWN_PROTOCOLS: false,
      USE_PROFILES: { html: true }
    });
  };

  // Sanitize both the full content and preview
  // Apply double sanitization - first with DOMPurify then with ReactMarkdown's config
  const sanitizedFullContent = sanitizeContent(data);
  const sanitizedPreviewContent = sanitizeContent(previewContent);

  return (
    <div className="relative">
      <div className={`bg-primary-700/10 p-4 rounded-lg overflow-x-auto transition-all duration-300 ${
        isExpanded ? 'max-h-[48rem]' : 'max-h-32'
      }`}>
        <div className={`prose prose-invert ${sizeClasses[size]} max-w-none`}>
          {/* Using sanitized content with security configuration */}
          <ReactMarkdown {...safeMarkdownConfig}>
            {isExpanded ? sanitizedFullContent : sanitizedPreviewContent}
          </ReactMarkdown>
        </div>
      </div>
  
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 mt-2 text-primary-text hover:text-primary-text/80 transition-colors text-xs"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span>{isExpanded ? 'Collapse' : 'Expand'} Content</span>
      </button>
    </div>
  );
};

export default MarkdownDataResult;
